import React, { useState, useEffect } from 'react';
import api from '../../services/api';

function CampaignPreviewTab({ practiceId }) {
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [cadences, setCadences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (practiceId) {
      api.getCampaignTypes(practiceId).then(res => {
        setCampaignTypes(res.data || []);
      }).catch(() => {});
    }
  }, [practiceId]);

  const handlePreview = async () => {
    if (!selectedTypeId) return;
    setLoading(true);
    setError(null);
    setEligibility(null);
    setCadences([]);

    try {
      const [eligRes, cadRes] = await Promise.all([
        api.previewCampaignTypeEligibility(selectedTypeId, practiceId),
        api.getCampaignCadences(selectedTypeId),
      ]);

      if (eligRes.success) {
        setEligibility(eligRes.data);
      } else {
        setError(eligRes.error || 'Failed to load eligibility preview');
      }

      if (cadRes.success) {
        setCadences(cadRes.data || []);
      }
    } catch (err) {
      setError(err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = campaignTypes.find(ct => ct.id === parseInt(selectedTypeId));
  const totalSteps = cadences.length;
  const estimatedMessages = eligibility ? eligibility.eligible_count * totalSteps : 0;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Campaign Type Selection</h3>

        <div style={styles.formGroup}>
          <label style={styles.label}>Campaign Type</label>
          <select
            style={styles.select}
            value={selectedTypeId}
            onChange={(e) => { setSelectedTypeId(e.target.value); setEligibility(null); setCadences([]); }}
          >
            <option value="">Select a campaign type...</option>
            {campaignTypes.map(ct => (
              <option key={ct.id} value={ct.id}>
                {ct.name || ct.slug}
              </option>
            ))}
          </select>
        </div>

        {selectedType && (
          <div style={styles.typeBadge}>
            <span>Eligibility: <strong>{selectedType.eligibility_type || 'custom'}</strong></span>
            {selectedType.sms_enabled && <span style={styles.channelTag}>SMS</span>}
            {selectedType.email_enabled && <span style={styles.channelTag}>Email</span>}
            {selectedType.voice_enabled && <span style={styles.channelTag}>Voice</span>}
          </div>
        )}

        <button
          style={{
            ...styles.previewButton,
            opacity: !selectedTypeId || loading ? 0.6 : 1,
            marginTop: '16px',
          }}
          onClick={handlePreview}
          disabled={!selectedTypeId || loading}
        >
          {loading ? 'Loading Preview...' : 'Preview Eligibility'}
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>{error}</div>
      )}

      {eligibility && (
        <>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Eligibility Results</h3>

            <div style={styles.statsRow}>
              <div style={styles.stat}>
                <span style={styles.statLabel}>Eligible Patients</span>
                <span style={styles.statValueLarge}>{eligibility.eligible_count}</span>
              </div>
              <div style={styles.stat}>
                <span style={styles.statLabel}>Cadence Steps</span>
                <span style={styles.statValueLarge}>{totalSteps}</span>
              </div>
              <div style={styles.stat}>
                <span style={styles.statLabel}>Est. Messages</span>
                <span style={styles.statValueLarge}>{estimatedMessages}</span>
              </div>
            </div>

            {eligibility.eligibility_type && (
              <div style={styles.ruleBox}>
                Rule: <strong>{eligibility.eligibility_type}</strong>
                {eligibility.config && (
                  <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                    ({Object.entries(eligibility.config).map(([k, v]) => `${k}: ${v}`).join(', ')})
                  </span>
                )}
              </div>
            )}

            {eligibility.records && eligibility.records.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={styles.sectionTitle}>
                  Sample Patients ({Math.min(eligibility.records.length, 10)} of {eligibility.eligible_count})
                </h4>
                <div style={styles.table}>
                  <div style={styles.tableHeader}>
                    <span style={{ ...styles.tableCell, flex: 2 }}>Name</span>
                    <span style={styles.tableCell}>Phone</span>
                    <span style={styles.tableCell}>Last Visit</span>
                  </div>
                  {eligibility.records.slice(0, 10).map((rec, i) => (
                    <div key={i} style={styles.tableRow}>
                      <span style={{ ...styles.tableCell, flex: 2, fontWeight: 500 }}>
                        {rec.first_name} {rec.last_name}
                      </span>
                      <span style={styles.tableCell}>{rec.phone || '-'}</span>
                      <span style={styles.tableCell}>
                        {rec.last_visit_date || rec.last_appointment_date || '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!eligibility.records || eligibility.records.length === 0) && eligibility.eligible_count === 0 && (
              <div style={styles.emptyText}>No eligible patients found for this campaign type.</div>
            )}
          </div>

          {cadences.length > 0 && (
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Cadence Steps</h3>

              {cadences.map((step, idx) => (
                <div key={step.id || idx} style={styles.cadenceStep}>
                  <div style={styles.cadenceHeader}>
                    <span style={styles.cadenceNumber}>Step {step.retry_count ?? idx}</span>
                    <span style={styles.cadenceDelay}>
                      Day {step.delay_days ?? 0}
                    </span>
                    <div style={styles.cadenceChannels}>
                      {step.sms_template_id && <span style={styles.channelTag}>SMS</span>}
                      {step.email_template_id && <span style={styles.channelTag}>Email</span>}
                      {!step.sms_template_id && !step.email_template_id && (
                        <span style={styles.noTemplate}>No template configured</span>
                      )}
                    </div>
                  </div>
                  {step.sms_template_name && (
                    <div style={styles.cadenceTemplate}>
                      SMS: {step.sms_template_name}
                    </div>
                  )}
                  {step.email_template_name && (
                    <div style={styles.cadenceTemplate}>
                      Email: {step.email_template_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Volume Estimate</h3>
            <div style={styles.volumeBox}>
              <p style={styles.volumeText}>
                ~{eligibility.eligible_count} patients x {totalSteps} step{totalSteps !== 1 ? 's' : ''} = ~{estimatedMessages} total messages
              </p>
              <p style={styles.volumeNote}>
                Actual volume depends on patient responses, opt-outs, and successful bookings between steps.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '20px 0',
    overflowY: 'auto',
    height: '100%',
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
  },
  cardTitle: {
    margin: '0 0 16px',
    fontSize: '15px',
    fontWeight: 600,
    color: '#111827',
  },
  sectionTitle: {
    margin: '0 0 10px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  formGroup: {
    marginBottom: '12px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '4px',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#111827',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  typeBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '13px',
    color: '#374151',
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    marginTop: '8px',
  },
  channelTag: {
    padding: '2px 8px',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
  },
  previewButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  errorBox: {
    marginBottom: '16px',
    padding: '10px 14px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '6px',
    fontSize: '13px',
    border: '1px solid #fecaca',
  },
  statsRow: {
    display: 'flex',
    gap: '32px',
    marginBottom: '16px',
    padding: '16px 20px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statValueLarge: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
  },
  ruleBox: {
    fontSize: '13px',
    color: '#374151',
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
  },
  table: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    backgroundColor: '#f9fafb',
    padding: '8px 12px',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'flex',
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '13px',
    color: '#374151',
  },
  tableCell: {
    flex: 1,
  },
  emptyText: {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
    padding: '20px',
  },
  cadenceStep: {
    padding: '12px 16px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    marginBottom: '8px',
  },
  cadenceHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  cadenceNumber: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  cadenceDelay: {
    fontSize: '13px',
    color: '#6b7280',
  },
  cadenceChannels: {
    display: 'flex',
    gap: '6px',
    marginLeft: 'auto',
  },
  noTemplate: {
    fontSize: '12px',
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  cadenceTemplate: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '6px',
    paddingLeft: '4px',
  },
  volumeBox: {
    padding: '16px 20px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #bbf7d0',
  },
  volumeText: {
    margin: '0 0 6px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#166534',
  },
  volumeNote: {
    margin: 0,
    fontSize: '13px',
    color: '#6b7280',
  },
};

export default CampaignPreviewTab;
